import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import { useAuthStore } from '@/store/authStore';
import { loginSchema, LoginFormData } from '@/schemas/loginSchema';

const Login = () => {
    const adminIdRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    const { login, isLoading, isAuthenticated, checkTokenExpiry } =
        useAuthStore();

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        mode: 'onChange',
    });

    useEffect(() => {
        const isTokenValid = checkTokenExpiry();

        // 이미 로그인되어 있으면 대시보드로 리다이렉트
        if (isAuthenticated && isTokenValid) {
            navigate('/dashboard');
            return;
        }

        if (adminIdRef.current) {
            adminIdRef.current.focus();
        }
    }, [isAuthenticated, checkTokenExpiry, navigate]);

    const onSubmit = async (data: LoginFormData) => {
        if (isLoading) return;

        const result = await login({
            adminId: data.adminId.trim(),
            adminPw: data.adminPw.trim(),
        });

        if (result.success) {
            navigate('/dashboard');
        } else {
            // 로그인 실패 시 폼 에러 표시
            setError('root', {
                type: 'manual',
                message: result.error || '로그인에 실패했습니다.',
            });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit(onSubmit)();
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // 로그인 페이지 JSX는 동일하게 유지
    return (
        <>
            <div className="">
                <video
                    className={styles.videoBackground}
                    autoPlay
                    muted
                    loop
                    playsInline
                >
                    <source src="/video/bg_video_1.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>

                <div className={styles.videoOverlay}></div>

                <div className="container-lg">
                    <div className="row justify-content-center authentication authentication-basic align-items-center h-100">
                        <div className="col-xxl-4 col-md-6 col-sm-8 col-12">
                            <div
                                className={`card custom-card my-4 ${styles.cardLogin}`}
                            >
                                <div className="card-body">
                                    <div className="row mx-0 align-items-center">
                                        <div className="p-3">
                                            <div className="text-center">
                                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                                    <img
                                                        className={
                                                            styles.loginLogo
                                                        }
                                                        src="/images/common/logo/logo_lite.png"
                                                        alt="AIS Logo"
                                                    />
                                                </h2>
                                                <p className="mb-4 text-white op-7 fw-normal text-center">
                                                    AIS Backoffice System.
                                                </p>
                                            </div>

                                            <form
                                                onSubmit={handleSubmit(
                                                    onSubmit,
                                                )}
                                                noValidate
                                            >
                                                <div className="row gy-3">
                                                    <div className="col-xl-12">
                                                        <label
                                                            className="form-label text-white"
                                                            htmlFor="admin_id"
                                                        >
                                                            Admin ID
                                                            <sup className="fs-12 text-danger">
                                                                *
                                                            </sup>
                                                        </label>
                                                        <input
                                                            {...register(
                                                                'adminId',
                                                            )}
                                                            ref={e => {
                                                                register(
                                                                    'adminId',
                                                                ).ref(e);
                                                                adminIdRef.current =
                                                                    e;
                                                            }}
                                                            className={`form-control ${errors.adminId ? 'is-invalid' : ''}`}
                                                            id="admin_id"
                                                            placeholder="Enter your admin id"
                                                            type="text"
                                                            onKeyDown={
                                                                handleKeyPress
                                                            }
                                                            disabled={isLoading}
                                                            autoComplete="username"
                                                        />
                                                        {errors.adminId && (
                                                            <div className="invalid-feedback text-white">
                                                                {
                                                                    errors
                                                                        .adminId
                                                                        .message
                                                                }
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="col-xl-12">
                                                        <label
                                                            className="form-label text-white"
                                                            htmlFor="admin_pw"
                                                        >
                                                            Password
                                                            <sup className="fs-12 text-danger">
                                                                *
                                                            </sup>
                                                        </label>
                                                        <div className="input-group">
                                                            <input
                                                                {...register(
                                                                    'adminPw',
                                                                )}
                                                                className={`form-control ${errors.adminPw ? 'is-invalid' : ''}`}
                                                                id="admin_pw"
                                                                placeholder="Enter your password"
                                                                type={
                                                                    showPassword
                                                                        ? 'text'
                                                                        : 'password'
                                                                }
                                                                onKeyDown={
                                                                    handleKeyPress
                                                                }
                                                                disabled={
                                                                    isLoading
                                                                }
                                                                autoComplete="current-password"
                                                            />
                                                            <button
                                                                className="btn btn-primary show-password-button"
                                                                type="button"
                                                                onClick={
                                                                    togglePasswordVisibility
                                                                }
                                                                disabled={
                                                                    isLoading
                                                                }
                                                                aria-label={
                                                                    showPassword
                                                                        ? 'Hide password'
                                                                        : 'Show password'
                                                                }
                                                            >
                                                                <i
                                                                    className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} align-middle`}
                                                                ></i>
                                                            </button>
                                                            {errors.adminPw && (
                                                                <div className="invalid-feedback text-white">
                                                                    {
                                                                        errors
                                                                            .adminPw
                                                                            .message
                                                                    }
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="col-xl-12">
                                                        <div className="form-check d-flex justify-content-between">
                                                            <div>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id="rememberMe"
                                                                    checked={
                                                                        rememberMe
                                                                    }
                                                                    onChange={e =>
                                                                        setRememberMe(
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        isLoading
                                                                    }
                                                                />
                                                                <label
                                                                    className="form-check-label text-white fw-normal fs-12"
                                                                    htmlFor="rememberMe"
                                                                >
                                                                    Remember me
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="d-grid mt-4">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={isLoading}
                                                    >
                                                        {isLoading ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm me-2"
                                                                    role="status"
                                                                    aria-hidden="true"
                                                                ></span>
                                                                Signing In...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-login-circle-line lh-1 me-2 align-middle"></i>
                                                                Sign In
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </form>

                                            <div className="text-center">
                                                <p className="text-white mt-3 mb-0">
                                                    If you are curious about
                                                    AIS,{' '}
                                                    <a
                                                        className="text-green fw-medium text-decoration-underline"
                                                        href="https://www.aiskorea.co.kr"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        click here
                                                    </a>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
